import { useState, useEffect } from "react";
import { MdAddHome, MdDelete } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { updateHospitalList } from "../../../../Service/RM";
import { showToast } from "../../../../reusableUI/toaster";
import {
  getLookupData,
  getStates,
  getDistricts,
  getPincodeDetails,
} from "../../../../Service/lookup";
import Loader from "../../../../reusableUI/loader";
import { EditHospitalDetails } from "../../../../Service/partnerAPI";
import AddressDetailForm from "../../../../reusableUI/address";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";

const AddHospital = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editUser = location?.state?.editUser || null;
  const [editedUser, setEditedUser] = useState(editUser);

  // Common state
  const [onboard, setOnBoard] = useState("");
  const [typeOf, setTypeOf] = useState("");
  const [hospitalName, setHospitalName] = useState("");
  const [hospitalSlogan, setHospitalSlogan] = useState("");
  const [noOfPatient, setNoOfPatient] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [country, setCountry] = useState("");
  const [casedata, setCase] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const [fullAddress, setFullAddress] = useState(null);

  // NEW: inline validation error states
  const [servicesError, setServicesError] = useState("");
  const [addressError, setAddressError] = useState("");

  // -------------------- SERVICES --------------------
  const SERVICES_PAYLOAD_KEY = "services";

  const normalize = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();

  const SERVICE_OPTIONS = [
    { key: "doctorVisits", value: "doctor visits", label: "Doctor visits" },
    {
      key: "physiotherapist",
      value: "physiotherapist",
      label: "Physiotherapist",
    },
    { key: "lab", value: "lab", label: "Lab" },
    { key: "pharmacy", value: "pharmacy", label: "Pharmacy" },
    {
      key: "equipmentRental",
      value: "equipment rental",
      label: "Equipment rental",
    },
  ];

  const NURSING_TYPE_OPTIONS = [
    { label: "Nurse", value: "nurse" },
    { label: "Nursing Assistant", value: "nursing assistant" },
  ];
  const NURSING_COMMISSION_KEY = "nursing-care";

  const defaultServicesState = SERVICE_OPTIONS.reduce((acc, s) => {
    acc[s.key] = false;
    return acc;
  }, {});

  const [services, setServices] = useState({ ...defaultServicesState });
  const [nursingCareTypes, setNursingCareTypes] = useState([]);

  const toggleService = (key) => {
    setServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleNursingCareType = (value) => {
    const v = normalize(value);
    setNursingCareTypes((prev) =>
      prev.includes(v) ? prev.filter((t) => t !== v) : [...prev, v],
    );
  };

  const allServicesSelected = () => {
    const allBase = SERVICE_OPTIONS.every((s) => !!services[s.key]);
    const allNursing = NURSING_TYPE_OPTIONS.every((t) =>
      nursingCareTypes.includes(normalize(t.value)),
    );
    return allBase && allNursing;
  };

  const toggleAllServices = () => {
    if (allServicesSelected()) {
      setServices({ ...defaultServicesState });
      setNursingCareTypes([]);
    } else {
      const next = { ...defaultServicesState };
      SERVICE_OPTIONS.forEach((s) => (next[s.key] = true));
      setServices(next);
      setNursingCareTypes(NURSING_TYPE_OPTIONS.map((t) => normalize(t.value)));
    }
  };

  const hasAtLeastOneServiceSelected = () => {
    const hasBaseService = SERVICE_OPTIONS.some((s) => !!services[s.key]);
    const hasNursingService = nursingCareTypes.length > 0;
    return hasBaseService || hasNursingService;
  };

  const validateServicesSelection = () => {
    if (!hasAtLeastOneServiceSelected()) {
      const msg = "Please select at least one service.";
      showToast(msg, "Error");
      setServicesError(msg);
      return false;
    }
    setServicesError("");
    return true;
  };

  const buildServicesPayload = () => {
    const serviceRows = [];

    SERVICE_OPTIONS.forEach((s) => {
      if (!services[s.key]) return;
      const name = normalize(s.value);
      const commissionValue = String(serviceCommissions?.[name] ?? "").trim();
      serviceRows.push({
        name,
        commission: Number(commissionValue || 0),
      });
    });

    nursingCareTypes.forEach((nursingType) => {
      const name = normalize(nursingType);
      const commissionValue = String(
        serviceCommissions?.[NURSING_COMMISSION_KEY] ?? "",
      ).trim();
      serviceRows.push({
        name,
        commission: Number(commissionValue || 0),
      });
    });

    return serviceRows;
  };

  const setServicesFromUser = (u, fallbackCommission = "") => {
    const possible =
      u?.[SERVICES_PAYLOAD_KEY] ||
      u?.servicesAvailable ||
      u?.providerServices ||
      u?.services ||
      u?.serviceList ||
      u?.serviceAvailability;

    setServices({ ...defaultServicesState });
    setNursingCareTypes([]);
    setServiceCommissions({});
    setServicesError("");

    if (!possible) return;

    if (Array.isArray(possible)) {
      const normalizedList = possible
        .map((item) =>
          typeof item === "string"
            ? normalize(item)
            : normalize(
                item?.name ||
                  item?.value ||
                  item?.label ||
                  item?.serviceType ||
                  item?.service,
              ),
        )
        .filter(Boolean);

      const next = { ...defaultServicesState };
      SERVICE_OPTIONS.forEach((opt) => {
        next[opt.key] = normalizedList.includes(normalize(opt.value));
      });
      setServices(next);

      const nursingAllowed = NURSING_TYPE_OPTIONS.map((x) =>
        normalize(x.value),
      );
      const foundNursing = Array.from(
        new Set(normalizedList.filter((x) => nursingAllowed.includes(x))),
      );
      setNursingCareTypes(foundNursing);

      const nextCommissions = {};
      possible.forEach((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) return;
        const name = normalize(
          item?.name ||
            item?.value ||
            item?.label ||
            item?.serviceType ||
            item?.service,
        );
        if (!name) return;

        const commissionRaw = String(
          item?.commission ?? item?.ceviliyarCommission ?? "",
        ).trim();
        if (!isValidCommissionValue(commissionRaw)) return;

        if (nursingAllowed.includes(name)) {
          if (!nextCommissions[NURSING_COMMISSION_KEY]) {
            nextCommissions[NURSING_COMMISSION_KEY] = commissionRaw;
          }
          return;
        }
        nextCommissions[name] = commissionRaw;
      });

      const fallback = String(fallbackCommission || "").trim();
      if (isValidCommissionValue(fallback)) {
        SERVICE_OPTIONS.forEach((opt) => {
          const key = normalize(opt.value);
          if (next[opt.key] && !nextCommissions[key]) {
            nextCommissions[key] = fallback;
          }
        });
        if (
          foundNursing.length > 0 &&
          !nextCommissions[NURSING_COMMISSION_KEY]
        ) {
          nextCommissions[NURSING_COMMISSION_KEY] = fallback;
        }
      }

      setServiceCommissions(nextCommissions);

      return;
    }

    if (typeof possible === "object") {
      const next = { ...defaultServicesState };

      SERVICE_OPTIONS.forEach((opt) => {
        if (possible?.[opt.key] === true || possible?.[opt.value] === true) {
          next[opt.key] = true;
        }
      });

      const foundNursing = [];
      NURSING_TYPE_OPTIONS.forEach((t) => {
        if (
          possible?.[t.value] === true ||
          possible?.[t.label] === true ||
          possible?.[normalize(t.value)] === true
        ) {
          foundNursing.push(normalize(t.value));
        }
      });

      setServices(next);
      setNursingCareTypes(foundNursing);

      const fallback = String(fallbackCommission || "").trim();
      if (isValidCommissionValue(fallback)) {
        const nextCommissions = {};
        SERVICE_OPTIONS.forEach((opt) => {
          if (next[opt.key]) {
            nextCommissions[normalize(opt.value)] = fallback;
          }
        });
        if (foundNursing.length > 0) {
          nextCommissions[NURSING_COMMISSION_KEY] = fallback;
        }
        setServiceCommissions(nextCommissions);
      }
    }
  };

  // -------------------- Partner (individual) section --------------------
  const [title, setTitle] = useState("");
  const [fullName, setFullName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCode, setPhoneCode] = useState("91");
  const [phone, setPhone] = useState("");
  const [areaInchargeState, setAreaInchargeState] = useState("");
  const [areaInchargeCity, setAreaInchargeCity] = useState("");
  const [commission, setCommission] = useState("");
  const [serviceCommissions, setServiceCommissions] = useState({});
  const [commissionErrors, setCommissionErrors] = useState({});
  const [showCommissionValidation, setShowCommissionValidation] =
    useState(false);
  const [frequency, setFrequency] = useState("daily");

  const defaultContact = {
    department: "Admin",
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    code: "91",
    phone: "",
  };

  const [contacts, setContacts] = useState(
    Array(2)
      .fill()
      .map(() => ({ ...defaultContact })),
  );

  const [team, setTeam] = useState([
    {
      speciality: "",
      title: "",
      fullName: "",
      dob: "",
      code: "91",
      phone: "",
    },
  ]);

  useEffect(() => {
    getCase();
    getStateList();

    if (editedUser) {
      openEdit();
    }

    if (areaInchargeState?.trim()) {
      getDistrictList(areaInchargeState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaInchargeState, editedUser]);

  // clear address error if user selects from suggestions (coords & fullAddress set)
  useEffect(() => {
    if (coords && fullAddress && addressError) {
      setAddressError("");
    }
  }, [coords, fullAddress, addressError]);

  const getCase = async () => {
    const result = await getLookupData();
    if (result.error === false) {
      setCase(result.data);
    } else {
      console.log("Error fetching lookup data");
    }
  };

  const getPincode = async (code) => {
    try {
      const result = await getPincodeDetails(code);
      if (result && result.error === false) {
        setState(result.data.state);
        setCity(result.data.district);
      } else {
        console.log("Error fetching pincode");
      }
    } catch (error) {
      console.error("Exception while fetching pincode:", error);
    }
  };

  const getStateList = async () => {
    try {
      const result = await getStates();
      if (result && result.error === false) {
        setStateList(result.data);
      } else {
        console.log("Error fetching states");
      }
    } catch (error) {
      console.error("Exception while fetching states:", error);
    }
  };

  const getDistrictList = async (selectedState) => {
    try {
      const result = await getDistricts(selectedState);
      if (result && result.error === false) {
        setDistrictList(result.data);
      } else {
        console.log("Error fetching districts");
      }
    } catch (error) {
      console.error("Exception while fetching districts:", error);
    }
  };

  const caseList = casedata.filter((item) => item.type === "phonecode");
  const caseTitle = casedata.filter((item) => item.type === "title");

  const handleChange = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index][field] = value;
    setContacts(updatedContacts);
  };

  const handleAddContact = () => {
    setContacts([
      ...contacts,
      {
        ...defaultContact,
      },
    ]);
  };

  const handleRemoveContact = (index) => {
    const updated = contacts.filter((_, i) => i !== index);
    setContacts(updated);
  };

  // -------------------- COMMISSION HELPERS --------------------
  const isValidCommissionValue = (value = "") => {
    const cleaned = String(value ?? "").trim();
    const regex = /^(?:\d{1,2})(?:\.\d{1,2})?$/;

    if (!regex.test(cleaned)) return false;

    const num = Number(cleaned);
    if (Number.isNaN(num) || num < 0 || num >= 100) return false;
    return true;
  };

  const handleServiceCommissionChange = (serviceValue, value) => {
    const input = String(value || "");

    if (input === "") {
      setServiceCommissions((prev) => ({ ...prev, [serviceValue]: "" }));
      if (showCommissionValidation) {
        setCommissionErrors((prev) => ({
          ...prev,
          [serviceValue]: "Commission is required for selected service.",
        }));
      }
      return;
    }

    const regex = /^(?:\d{0,2})(?:\.\d{0,2})?$/;
    if (!regex.test(input)) return;

    const numeric = Number(input);
    if (!Number.isNaN(numeric) && numeric >= 100) return;

    setServiceCommissions((prev) => ({ ...prev, [serviceValue]: input }));
    if (showCommissionValidation) {
      setCommissionErrors((prev) => ({ ...prev, [serviceValue]: "" }));
    }
  };

  const getSelectedServiceCommissionKeys = () => {
    const base = SERVICE_OPTIONS.filter((s) => services[s.key]).map((s) =>
      normalize(s.value),
    );
    const nursing = nursingCareTypes.length > 0 ? [NURSING_COMMISSION_KEY] : [];
    return Array.from(new Set([...base, ...nursing])).filter(Boolean);
  };

  const getPrimaryCommission = () => {
    const selectedKeys = getSelectedServiceCommissionKeys();

    for (const key of selectedKeys) {
      const value = String(serviceCommissions?.[key] ?? "").trim();
      if (value && isValidCommissionValue(value)) {
        return Number(value);
      }
    }

    const fallback = String(commission ?? "").trim();
    if (fallback && isValidCommissionValue(fallback)) return Number(fallback);
    return 0;
  };

  const validateCommission = () => {
    setShowCommissionValidation(true);
    const selectedKeys = getSelectedServiceCommissionKeys();
    const nextErrors = {};

    selectedKeys.forEach((key) => {
      const value = String(serviceCommissions?.[key] ?? "").trim();

      if (!value) {
        nextErrors[key] = "Commission is required for selected service.";
        return;
      }

      if (!isValidCommissionValue(value)) {
        nextErrors[key] =
          "Commission must be below 100 and allow up to 2 decimal places.";
      }
    });

    setCommissionErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showToast(
        "Please fill valid commission for all selected services.",
        "Error",
      );
      return false;
    }

    return true;
  };

  // -------------------- VALIDATION HELPERS --------------------
  const validateHospitalCreate = () => {
    if (!onboard.trim()) {
      showToast("Provider type is required", "Error");
      return false;
    }

    if (!validateServicesSelection()) return false;

    if (!hospitalName.trim()) {
      showToast("Hospital Name is required", "Error");
      return false;
    }
    if (hospitalName.trim().length < 4) {
      showToast("Hospital Name must be at least 4 characters", "Error");
      return false;
    }
    if (!addressLine1.trim()) {
      showToast("Address Line 1 is required", "Error");
      return false;
    }
    // if (addressLine1.trim().length < 12 || addressLine1.trim().length > 255) {
    //   showToast("The Address must be between 12 and 255 characters.", "Error");
    //   return false;
    // }
    if (!state.trim()) {
      showToast("State is required", "Error");
      return false;
    }
    if (!city.trim()) {
      showToast("City is required", "Error");
      return false;
    }
    if (!pinCode.toString().trim()) {
      showToast("Pin Code is required", "Error");
      return false;
    }
    if (!/^\d{6,}$/.test(pinCode.toString().trim())) {
      showToast("Pin Code must be at least 6 digits.", "Error");
      return false;
    }

    // NEW: enforce address from suggestions (coords + fullAddress)
    if (!coords || !fullAddress) {
      const msg = "Please select an address from suggestions.";
      showToast(msg, "Error");
      setAddressError(msg);
      return false;
    }
    setAddressError("");

    if (!validateCommission()) return false;

    if (!frequency.trim()) {
      showToast("Frequency is required", "Error");
      return false;
    }
    if (!contacts[0]) {
      showToast("At least one contact is required.", "Error");
      return false;
    }

    const contact1 = contacts[0];
    const validateContact = (contact, index) => {
      if (!contact) return false;
      const contactNum = index + 1;

      if (!contact.department?.trim()) {
        showToast(`Contact ${contactNum}: Department is required.`, "Error");
        return false;
      }
      if (!contact.title?.trim()) {
        showToast(`Contact ${contactNum}: Title is required.`, "Error");
        return false;
      }
      if (!contact.firstName?.trim()) {
        showToast(`Contact ${contactNum}: First Name is required`, "Error");
        return false;
      }
      if (!contact.lastName?.trim()) {
        showToast(`Contact ${contactNum}: Last Name is required.`, "Error");
        return false;
      }
      if (!contact.phone?.trim()) {
        showToast(`Contact ${contactNum}: Phone Number is required.`, "Error");
        return false;
      }
      if (contact.phone.trim().length < 10) {
        showToast(
          `Contact ${contactNum}: Phone Number must be at least 10 digits.`,
          "Error",
        );
        return false;
      }
      if (!contact.email?.trim()) {
        showToast(`Contact ${contactNum}: Email id is required.`, "Error");
        return false;
      }
      return true;
    };

    if (!validateContact(contact1, 0)) return false;

    for (let i = 1; i < contacts.length; i++) {
      const contact = contacts[i];
      if (contact) {
        const hasData =
          contact.firstName?.trim() ||
          contact.lastName?.trim() ||
          contact.phone?.trim() ||
          contact.email?.trim();
        if (hasData) {
          if (!validateContact(contact, i)) return false;
        }
      }
    }

    return true;
  };

  const validatePartnerCreate = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!onboard?.trim())
      return (showToast("Onboarding info is required.", "Error"), false);

    if (!validateServicesSelection()) return false;

    if (!title?.trim())
      return (showToast("Title is required.", "Error"), false);
    if (!fullName?.trim())
      return (showToast("Full Name is required.", "Error"), false);
    if (!gender?.trim())
      return (showToast("Gender is required.", "Error"), false);
    if (!dob?.trim())
      return (showToast("Date of Birth is required.", "Error"), false);
    if (!areaInchargeState?.trim())
      return (showToast("Area Incharge State is required.", "Error"), false);
    if (!areaInchargeCity?.trim())
      return (showToast("Area Incharge City is required.", "Error"), false);
    if (!phone?.trim())
      return (showToast("Contact number is required.", "Error"), false);
    if (!/^\d{10}$/.test(phone))
      return (showToast("Phone number must be 10 digits.", "Error"), false);
    if (!email?.trim())
      return (showToast("Contact email is required.", "Error"), false);
    if (!emailPattern.test(email))
      return (showToast("Enter a valid email address.", "Error"), false);

    if (!validateCommission()) return false;

    if (!frequency?.trim())
      return (showToast("Frequency is required.", "Error"), false);
    if (!addressLine1?.trim())
      return (showToast("Address Line 1 is required.", "Error"), false);
    if (!state?.trim())
      return (showToast("State is required.", "Error"), false);
    if (!city?.trim()) return (showToast("City is required.", "Error"), false);
    if (!pinCode?.toString().trim())
      return (showToast("PIN Code is required.", "Error"), false);
    if (!/^\d{6}$/.test(pinCode.toString()))
      return (showToast("Enter a valid 6-digit PIN Code.", "Error"), false);

    // NEW: enforce address from suggestions (coords + fullAddress)
    if (!coords || !fullAddress) {
      const msg = "Please select an address from suggestions.";
      showToast(msg, "Error");
      setAddressError(msg);
      return false;
    }
    setAddressError("");

    if (onboard !== "individual") {
      const firstContact = contacts?.[0];
      if (
        !firstContact ||
        !firstContact.title?.trim() ||
        !firstContact.firstName?.trim() ||
        !firstContact.phone?.trim() ||
        !firstContact.email?.trim()
      ) {
        showToast(
          "At least one contact person is required (Title, Name, Phone, Email).",
          "Error",
        );
        return false;
      }
    }

    return true;
  };

  // -------------------- PAYLOAD BUILDERS --------------------
  const buildContactsPayload = () =>
    contacts
      .filter(
        (c) =>
          c.title?.trim() ||
          c.firstName?.trim() ||
          c.lastName?.trim() ||
          c.phone?.trim() ||
          c.email?.trim(),
      )
      .map((c) => ({
        name: `${c.title} ${c.firstName} ${c.middleName || ""} ${c.lastName || ""}`.trim(),
        phone: Number((c.code + c.phone).replace(/\s+/g, "")),
        email: c.email,
      }));

  const buildAddressPayload = (asNumber = false) => ({
    address1: addressLine1,
    address2: addressLine2,
    city,
    state,
    country: country || "India",
    pincode: asNumber ? Number(pinCode) : pinCode,
    coords,
    fullAddress,
  });

  const buildCreatePayload = () => {
    const contactInfo = buildContactsPayload();
    const servicesPayload = buildServicesPayload();

    if (onboard === "individual") {
      return {
        providerType: onboard,
        basicInfo: {
          title,
          name: `${title} ${fullName}`.trim(),
          gender,
          dob,
          phone: phoneCode + phone,
          email,
        },
        areaInchargeDetails: {
          state: areaInchargeState,
          city: areaInchargeCity,
        },
        contactInfo,
        addressInfo: buildAddressPayload(true),
        payout: {
          commission: getPrimaryCommission(),
          frequency,
        },
        [SERVICES_PAYLOAD_KEY]: servicesPayload,
      };
    }

    return {
      providerType: onboard,
      basicInfo: {
        name: hospitalName,
        slogan: hospitalSlogan,
        patientCount: Number(noOfPatient),
      },
      addressInfo: buildAddressPayload(false),
      contactInfo,
      payout: {
        commission: getPrimaryCommission(),
        frequency,
      },
      [SERVICES_PAYLOAD_KEY]: servicesPayload,
    };
  };

  // -------------------- CREATE HANDLER --------------------
  const handleCreate = async () => {
    if (onboard === "individual") {
      if (!validatePartnerCreate()) return;
    } else {
      if (!validateHospitalCreate()) return;
    }

    const payload = buildCreatePayload();
    console.log("Create payload:", payload);

    setLoading(true);
    try {
      const res = await updateHospitalList(payload);

      if (res.error === false) {
        showToast(
          onboard === "individual"
            ? "Partner profile added successfully."
            : "Hospital profile added successfully.",
          "Success",
        );

        setOnBoard("");
        setTitle("");
        setFullName("");
        setGender("");
        setDob("");
        setPhone("");
        setEmail("");
        setAreaInchargeState("");
        setAreaInchargeCity("");
        setHospitalName("");
        setHospitalSlogan("");
        setNoOfPatient("");
        setAddressLine1("");
        setAddressLine2("");
        setCity("");
        setState("");
        setPinCode("");
        setCountry("");
        setCommission("");
        setServiceCommissions({});
        setCommissionErrors({});
        setShowCommissionValidation(false);
        setFrequency("daily");
        setServices({ ...defaultServicesState });
        setNursingCareTypes([]);
        setContacts(
          Array(2)
            .fill()
            .map(() => ({ ...defaultContact })),
        );
        setCoords(null);
        setFullAddress(null);
        setAddressError("");
        setServicesError("");

        const userRole = secureLocalStorage.getItem("role");
        setTimeout(() => {
          if (userRole === "hospital") {
            navigate("/admin/partnerlist");
          } else {
            navigate("/admin/partnerlist");
          }
        }, 600);
      } else {
        showToast(res.message || "Something went wrong.", "Error");
      }
    } catch (error) {
      console.error("Create error:", error);
      showToast("Something went wrong while saving data.", "Error");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- EDIT SECTION --------------------
  const parseName = (name = "") => {
    const parts = name.trim().split(/\s+/);
    return {
      title: parts[0] || "",
      firstName: parts[1] || "",
      lastName: parts[2] || "",
    };
  };

  const openEdit = () => {
    const u = editedUser || editUser;
    if (!u) return;

    setOnBoard(u?.providerType || "");
    setTitle(
      u?.providerType === "individual"
        ? u?.basicInfo?.name?.split(" ")[0] || ""
        : "",
    );
    setGender(u?.basicInfo?.gender || "");
    setEmail(u?.basicInfo?.email || "");
    setPhoneCode("91");
    setPhone(u?.basicInfo?.phone ? String(u.basicInfo.phone).slice(-10) : "");
    setFullName(u?.basicInfo?.name || "");
    setDob(
      u?.basicInfo?.dob ? moment(u.basicInfo.dob).format("YYYY-MM-DD") : "",
    );
    setHospitalName(u?.basicInfo?.name || "");
    setHospitalSlogan(u?.basicInfo?.slogan || "");
    setBannerImage(u?.basicInfo?.bannerImage || "");
    setNoOfPatient(u?.basicInfo?.patientCount || "");
    setAddressLine1(u?.addressInfo?.address1 || "");
    setAddressLine2(u?.addressInfo?.address2 || "");
    setState(u?.addressInfo?.state || "");
    setCity(u?.addressInfo?.city || "");
    setPinCode(u?.addressInfo?.pincode || "");
    setCountry(u?.addressInfo?.country || "");
    setCoords(u?.addressInfo?.coords || null);
    setFullAddress(u?.addressInfo?.fullAddress || null);
    const currentCommission =
      u?.payout?.commission !== undefined && u?.payout?.commission !== null
        ? String(u.payout.commission)
        : "";
    setCommission(currentCommission);
    setFrequency(u?.payout?.frequency || "daily");
    setAreaInchargeCity(
      u?.areaIncharge?.city || u?.areaInchargeDetails?.city || "",
    );
    setAreaInchargeState(
      u?.areaIncharge?.state || u?.areaInchargeDetails?.state || "",
    );

    setServicesFromUser(u, currentCommission);
    setCommissionErrors({});
    setShowCommissionValidation(false);
    setAddressError("");
    setServicesError("");

    const mappedContacts =
      u?.contactInfo?.map((item) => {
        const { title, firstName, lastName } = parseName(item?.name || "");
        return {
          department: "Admin",
          title,
          firstName,
          middleName: "",
          lastName: lastName || "",
          email: item?.email || "",
          code: "91",
          phone: item?.phone ? String(item.phone).slice(-10) : "",
        };
      }) || [];

    while (mappedContacts.length < 2) {
      mappedContacts.push({ ...defaultContact });
    }

    setContacts(mappedContacts);
  };

  const handleEditSave = async () => {
    const baseUser = editedUser || editUser;
    if (!baseUser) return;

    if (onboard === "individual") {
      if (!validatePartnerCreate()) return;
    } else {
      if (!validateHospitalCreate()) return;
    }

    const userRole = secureLocalStorage.getItem("role");
    const updatedUser = JSON.parse(JSON.stringify(baseUser));
    const isIndividual = baseUser?.providerType === "individual";

    updatedUser.basicInfo = updatedUser.basicInfo || {};
    updatedUser.basicInfo.name = isIndividual ? fullName : hospitalName;
    updatedUser.basicInfo.bannerImage =
      bannerImage || editUser?.basicInfo?.bannerImage;
    updatedUser.basicInfo.slogan = hospitalSlogan;
    updatedUser.basicInfo.patientCount = Number(noOfPatient) || 0;
    if (gender) updatedUser.basicInfo.gender = gender;
    if (dob) updatedUser.basicInfo.dob = dob;
    if (email) updatedUser.basicInfo.email = email;
    if (phone) updatedUser.basicInfo.phone = phone;

    updatedUser.addressInfo = updatedUser.addressInfo || {};
    updatedUser.addressInfo.address1 = addressLine1;
    updatedUser.addressInfo.address2 = addressLine2;
    updatedUser.addressInfo.city = city;
    updatedUser.addressInfo.state = state;
    updatedUser.addressInfo.country =
      country || updatedUser.addressInfo.country || "India";
    updatedUser.addressInfo.pincode = pinCode;
    updatedUser.addressInfo.coords =
      coords != null ? coords : updatedUser.addressInfo.coords;
    updatedUser.addressInfo.fullAddress =
      fullAddress != null ? fullAddress : updatedUser.addressInfo.fullAddress;

    updatedUser.contactInfo = buildContactsPayload();

    updatedUser.payout = updatedUser.payout || {};
    updatedUser.payout.commission = getPrimaryCommission();
    if (frequency) updatedUser.payout.frequency = frequency;

    updatedUser.areaIncharge = updatedUser.areaIncharge || {};
    if (areaInchargeState) updatedUser.areaIncharge.state = areaInchargeState;
    if (areaInchargeCity) updatedUser.areaIncharge.city = areaInchargeCity;

    updatedUser[SERVICES_PAYLOAD_KEY] = buildServicesPayload();

    console.log("UpdatedUser for edit:", updatedUser, baseUser?._id);

    try {
      setLoading(true);
      const res = await EditHospitalDetails(baseUser?._id, updatedUser);

      if (res.error === false) {
        showToast("Profile edited successfully.", "Success");
        if (userRole === "hospital") {
          navigate("/admin/partnerlist");
        } else {
          navigate("/admin/partnerlist");
        }
      } else {
        showToast(res.message || "Failed to update hospital profile.", "Error");
      }
    } catch (error) {
      console.error("Edit error:", error);
      showToast("Something went wrong while editing hospital.", "Error");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- UI HELPERS --------------------
  const renderServicesSection = () => {
    const boxClass =
      "border border-[#E4E4E7] rounded-md px-4 py-3 bg-white min-h-[64px] flex items-center";
    const nursingBoxClass =
      "border border-[#E4E4E7] rounded-md px-4 py-3 bg-white min-h-[64px] flex flex-col justify-center";

    return (
      <div className="mb-6">
        <h3 className="text-sm font-poppins font-semibold mb-3 text-[#3B3B3B]">
          Services Availability & Ceviliyar Commission{" "}
          <span className="text-red-500">*</span>
        </h3>

        <div className="border rounded-sm p-4">
          <label className="flex items-center gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-[#4951EC]"
              checked={allServicesSelected()}
              onChange={toggleAllServices}
            />
            <span className="text-[13px] text-[#3B3B3B] font-poppins font-medium">
              Select All
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SERVICE_OPTIONS.map((opt) => {
              const commissionKey = normalize(opt.value);
              const checked = !!services[opt.key];
              const error =
                checked && showCommissionValidation
                  ? commissionErrors?.[commissionKey]
                  : "";

              return (
                <div
                  key={opt.key}
                  className={`${boxClass} flex-col items-start`}
                >
                  <div className="w-full flex items-center gap-3 justify-between">
                    <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-[#4951EC]"
                        checked={checked}
                        onChange={() => toggleService(opt.key)}
                      />
                      <span className="text-[14px] text-[#606060] font-poppins leading-none truncate">
                        {opt.label}
                      </span>
                    </label>

                    {checked && (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={serviceCommissions?.[commissionKey] ?? ""}
                          onChange={(e) =>
                            handleServiceCommissionChange(
                              commissionKey,
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          className="border border-1 border-[#D2D2D2] text-[13px] p-1 rounded-sm w-[95px]"
                        />
                        <span className="text-[13px] text-[#606060] font-poppins">
                          %
                        </span>
                      </div>
                    )}
                  </div>
                  {checked && error ? (
                    <p className="text-[11px] text-red-500 font-poppins mt-1">
                      {error}
                    </p>
                  ) : null}
                </div>
              );
            })}

            <div className={nursingBoxClass}>
              <div className="flex items-center justify-between mb-3 w-full">
                <span className="text-[14px] text-[#606060] font-poppins font-medium">
                  Nursing care
                </span>
                {nursingCareTypes.length > 0 && (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={serviceCommissions?.[NURSING_COMMISSION_KEY] ?? ""}
                      onChange={(e) =>
                        handleServiceCommissionChange(
                          NURSING_COMMISSION_KEY,
                          e.target.value,
                        )
                      }
                      placeholder="0"
                      className="border border-1 border-[#D2D2D2] text-[13px] p-1 rounded-sm w-[95px]"
                    />
                    <span className="text-[13px] text-[#606060] font-poppins">
                      %
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                {NURSING_TYPE_OPTIONS.map((t) => {
                  const checked = nursingCareTypes.includes(normalize(t.value));

                  return (
                    <div key={t.value} className="w-full">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 shrink-0 accent-[#4951EC]"
                          checked={checked}
                          onChange={() => toggleNursingCareType(t.value)}
                        />
                        <span className="text-[13px] text-[#606060] font-poppins whitespace-nowrap">
                          {t.label}
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
              {nursingCareTypes.length > 0 &&
              showCommissionValidation &&
              commissionErrors?.[NURSING_COMMISSION_KEY] ? (
                <p className="text-[11px] text-red-500 font-poppins mt-1">
                  {commissionErrors[NURSING_COMMISSION_KEY]}
                </p>
              ) : null}
            </div>
          </div>

          {servicesError ? (
            <p className="text-[11px] text-red-500 font-poppins mt-3">
              {servicesError}
            </p>
          ) : (
            <p className="text-[11px] text-[#7A7A7A] font-poppins mt-3">
              Select at least one service to continue.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderContactForm = (contact, index) => {
    const contactType =
      index === 0
        ? "Primary Contact"
        : index === 1
          ? "Secondary Contact"
          : `Contact Person ${index + 1}`;

    return (
      <div
        className="w-full mb-4 border-b last:border-b-0 pb-4 last:pb-0"
        key={index}
      >
        <div className="px-4 pt-2 flex justify-between items-center">
          <h4 className="text-[13px] font-poppins font-semibold text-gray-700">
            {contactType}
          </h4>
          {index >= 1 && (
            <button
              type="button"
              onClick={() => handleRemoveContact(index)}
              className="text-red-500 hover:text-red-700 text-[12px] font-poppins flex items-center gap-1 transition-colors bg-transparent border-none cursor-pointer"
            >
              <MdDelete size={16} />
              Remove
            </button>
          )}
        </div>
        <div className="p-4 rounded">
          <div
            className={`flex flex-row items-center gap-2 w-full ${
              typeOf === "single"
                ? "justify-start gap-3"
                : "justify-start gap-4"
            }`}
          >
            {onboard === "individual" && (
              <div>
                <label className="text-[13px] text-[#606060] mb-1 block font-poppins">
                  Department {index === 0 && <a className="text-[red]">*</a>}
                </label>
                <input
                  type="text"
                  className="border px-2 py-1.5 border-[#D2D2D2] rounded-r-sm rounded-sm text-[12px] font-poppins"
                  value={contact.department}
                  onChange={(e) => {
                    const value = e.target.value;
                    const capitalized =
                      value.charAt(0).toUpperCase() + value.slice(1);
                    handleChange(index, "department", capitalized);
                  }}
                />
              </div>
            )}

            <div>
              <div className="flex flex-row items-center gap-10">
                <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                  Title
                </label>
                <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                  Full Name {index === 0 && <a className="text-[red]">*</a>}
                </label>
              </div>
              <div className="flex">
                <div className="relative w-[60px]">
                  <select
                    className="appearance-none w-full border border-[#D2D2D2] px-3 py-1.5 rounded-sm text-[12px] text-[#606060] font-poppins"
                    value={contact.title}
                    onChange={(e) =>
                      handleChange(index, "title", e.target.value)
                    }
                  >
                    <option hidden></option>
                    {caseTitle
                      .filter((item) => item.label !== "Master.")
                      .map((item, index) => (
                        <option
                          key={`${item.value}-${index}`}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-[#606060]"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.66a.75.75 0 01-1.08 0l-4.25-4.66a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                <input
                  type="text"
                  className="border border-1 border-[#D2D2D2] px-2 py-1 rounded-r-sm text-[12px] font-poppins w-[200px]"
                  value={contact.firstName}
                  onChange={(e) => {
                    const val = e.target.value;
                    const capitalized =
                      val.charAt(0).toUpperCase() + val.slice(1);
                    handleChange(index, "firstName", capitalized);
                  }}
                />
              </div>
            </div>

            {onboard !== "individual" && (
              <div>
                <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                  Last Name {index === 0 && <a className="text-[red]">*</a>}
                </label>
                <input
                  type="text"
                  className="border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-r-sm text-[12px] font-poppins w-[200px]"
                  value={contact.lastName}
                  onChange={(e) => {
                    const val = e.target.value;
                    const capitalized =
                      val.charAt(0).toUpperCase() + val.slice(1);
                    handleChange(index, "lastName", capitalized);
                  }}
                />
              </div>
            )}

            <div>
              <div className="flex flex-row items-center gap-8">
                <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                  Phone {index === 0 && <a className="text-[red]">*</a>}
                </label>
              </div>
              <div className="flex">
                <select
                  className="w-[60px] border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-l-sm rounded-sm text-[12px] text-[#606060] font-poppins"
                  value={contact.code}
                  onChange={(e) => handleChange(index, "code", e.target.value)}
                >
                  {caseList.map((item, idx) => (
                    <option key={idx} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-r-sm text-[12px] font-poppins w-[200px]"
                  maxLength={10}
                  value={contact.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      handleChange(index, "phone", value);
                    }
                  }}
                />
              </div>
            </div>

            <div>
              <label className="text-[13px] text-[#606060] block mb-1 font-poppins">
                Email {index === 0 && <a className="text-[red]">*</a>}
              </label>
              <input
                type="email"
                className="border border-1 border-[#D2D2D2] w-[200px] px-2 py-1.5 rounded-sm text-[12px] font-poppins"
                value={contact.email}
                onChange={(e) => handleChange(index, "email", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const profilePage = localStorage.getItem("profilePage");

  return (
    <div>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="p-2">
          <div className="bg-[#4951EC] text-white p-4 rounded-t-[10px] flex justify-between items-center ">
            <div className="flex items-center gap-1">
              <MdAddHome size={22} />
              <h2 className="text-[14px] font-poppins">
                {!editedUser ? "Add Partner" : "Edit Partner"}
              </h2>
            </div>
            <div className="flex gap-2">
              {!editedUser ? (
                <button
                  className="border hover:bg-blue-700 text-white font-poppins text-[14px] px-4 py-1 rounded-sm"
                  onClick={handleCreate}
                >
                  Save
                </button>
              ) : (
                <button
                  className="border hover:bg-blue-700 text-white font-poppins text-[14px] px-4 py-1 rounded-sm"
                  onClick={handleEditSave}
                >
                  Update
                </button>
              )}
              <button
                className="bg-[#fff] text-[#4951EC] text-[14px] font-poppins px-4 py-1 rounded-sm"
                onClick={() => {
                  const confirmBack = window.confirm(
                    "Are you sure you want to go back?",
                  );
                  if (confirmBack) {
                    if (profilePage === "hospital") {
                      navigate("/admin/partnerlist");
                    } else {
                      navigate("/admin/partnerProfile");
                    }
                  }
                }}
              >
                Back
              </button>
            </div>
          </div>

          {onboard !== "individual" ? (
            <div className="bg-white overflow-y-auto h-[78vh] pb-[100px] p-4 rounded-b-[20px] shadow-lg">
              {profilePage !== "partnerSide" && (
                <h3 className="text-sm font-semibold mb-4 font-poppins text-[#3B3B3B]">
                  {typeOf === "entity" &&
                    onboard !== "hospital" &&
                    "Entity General Information"}
                  {onboard === "hospital" &&
                    typeOf !== "entity" &&
                    "Hospital General Information"}
                  {typeOf !== "entity" &&
                    onboard !== "hospital" &&
                    "General Information"}
                </h3>
              )}

              {profilePage !== "partnerSide" && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <div>
                      <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                        Onboard <a className="text-[red]">*</a>
                      </p>
                      <select
                        className="w-[75%] text-[12px] text-[#606060] border border-1 border-[#D2D2D2] font-poppins px-2 py-1.5 rounded-sm"
                        value={onboard}
                        onChange={(e) => setOnBoard(e.target.value)}
                      >
                        <option hidden>Provider Type</option>
                        <option value="agency">Agency</option>
                        <option value="company">Company</option>
                        <option value="hospital">Hospital</option>
                        <option value="individual">Individual</option>
                      </select>
                    </div>

                    <div>
                      <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                        {typeOf === "entity" &&
                          onboard !== "hospital" &&
                          "Entity Name"}
                        {onboard === "hospital" &&
                          typeOf !== "entity" &&
                          "Hospital Name"}
                        {typeOf !== "entity" &&
                          onboard !== "hospital" &&
                          "Name"}
                        <a className="text-[red] ml-1">*</a>
                      </p>
                      <input
                        type="text"
                        value={hospitalName}
                        onChange={(e) => {
                          const value = e.target.value;
                          const capitalized =
                            value.charAt(0).toUpperCase() + value.slice(1);
                          setHospitalName(capitalized);
                        }}
                        className="border border-1 border-[#D2D2D2] text-[12px] p-1.5 rounded-sm w-full font-poppins"
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                        {typeOf === "entity" &&
                          onboard !== "hospital" &&
                          "Entity Slogan"}
                        {onboard === "hospital" &&
                          typeOf !== "entity" &&
                          "Hospital Slogan"}
                        {typeOf !== "entity" &&
                          onboard !== "hospital" &&
                          "Slogan"}
                        <a className="text-[10px] text-[#5B60A8]">(optional)</a>
                      </p>
                      <input
                        type="text"
                        value={hospitalSlogan}
                        onChange={(e) => setHospitalSlogan(e.target.value)}
                        className="border border-1 border-[#D2D2D2] text-[14px] p-1 rounded-sm w-full"
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                        No of Patient{" "}
                        <a className="text-[10px] text-[#5B60A8] font-poppins">
                          (optional)
                        </a>
                      </p>
                      <input
                        type="number"
                        value={noOfPatient}
                        onChange={(e) => setNoOfPatient(e.target.value)}
                        className="border border-1 border-[#D2D2D2] text-[14px] p-1 rounded-sm w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {profilePage !== "partnerSide" && (
                <>
                  <AddressDetailForm
                    addressLine1={addressLine1}
                    setAddressLine1={setAddressLine1}
                    addressLine2={addressLine2}
                    setAddressLine2={setAddressLine2}
                    state={state}
                    setState={setState}
                    city={city}
                    setCity={setCity}
                    pinCode={pinCode}
                    setPinCode={setPinCode}
                    country={country}
                    setCountry={setCountry}
                    stateList={stateList}
                    districtList={districtList}
                    setCoords={setCoords}
                    setFullAddress={setFullAddress}
                  />
                  {addressError && (
                    <p className="text-[11px] text-red-500 font-poppins mt-1">
                      {addressError}
                    </p>
                  )}
                </>
              )}

              {renderServicesSection()}

              <div className="mb-6 flex flex-row">
                <div className="w-[25%]">
                  <p className="text-sm font-poppins font-semibold mb-1 text-[#3B3B3B]">
                    Frequency <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[60%] text-[12px] text-[#606060] border border-1 border-[#D2D2D2] font-poppins px-2 py-1.5 rounded-sm"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-poppins font-semibold text-[#3B3B3B]">
                  Contact Person Information
                </h3>
                <button
                  type="button"
                  onClick={handleAddContact}
                  className="px-4 py-1.5 border border-[#4951EC] rounded-md text-[12px] font-poppins text-[#4951EC] hover:bg-blue-50 transition-colors"
                >
                  + Add Contact
                </button>
              </div>
              <div className="border rounded-sm mb-4">
                {contacts.map((contact, idx) =>
                  renderContactForm(contact, idx),
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white overflow-y-auto h-[78vh] pb-[100px] p-4 rounded-b-[15px] shadow-lg">
              <h3 className="text-sm font-semibold mb-4 font-poppins text-[#3B3B3B]">
                Partner General Information
              </h3>

              <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
                <div>
                  <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                    Onboard <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[75%] text-[12px] text-[#606060] border border-1 border-[#D2D2D2] font-poppins px-2 py-1.5 rounded-sm"
                    value={onboard}
                    onChange={(e) => {
                      setOnBoard(e.target.value);
                      setTypeOf("");
                    }}
                  >
                    <option hidden>Provider Type</option>
                    <option value="hospital">Hospital</option>
                    <option value="company">Company</option>
                    <option value="agency">Agency</option>
                    <option value="individual">Individual</option>
                  </select>
                </div>

                <div>
                  <div className="flex flex-row items-center gap-10">
                    <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                      Title
                    </label>
                    <label className="text-[13px] font-poppins text-[#606060] block mb-1">
                      Full Name <a className="font-poppins text-[red]">*</a>
                    </label>
                  </div>
                  <div className="flex">
                    <div className="relative w-[60px]">
                      <select
                        className="appearance-none w-[70px] border border-[#D2D2D2] px-4 py-1.5 rounded-sm text-[12px] text-[#606060] font-poppins"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      >
                        <option hidden></option>
                        {caseTitle
                          .filter((item) => item.label !== "Master.")
                          .map((item, index) => (
                            <option
                              key={`${item.value}-${index}`}
                              value={item.value}
                            >
                              {item.label}
                            </option>
                          ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-[#606060]"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.66a.75.75 0 01-1.08 0l-4.25-4.66a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>

                    <input
                      type="text"
                      className="border border-1 border-[#D2D2D2] px-2 py-1 rounded-r-sm text-[12px] font-poppins ml-2"
                      value={fullName}
                      onChange={(e) => {
                        const val = e.target.value;
                        const capitalized =
                          val.charAt(0).toUpperCase() + val.slice(1);
                        setFullName(capitalized);
                      }}
                    />
                  </div>
                </div>

                <div className="ml-12">
                  <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                    Gender <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[90%] text-[12px] text-[#606060] font-poppins border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-sm"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option hidden>gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                    Date of birth <a className="text-[red]">*</a>
                  </p>
                  <input
                    type="date"
                    id="dob"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-[75%] text-[12px] text-[#606060] font-poppins border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-sm"
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>

              <h3 className="text-sm font-semibold mb-4 font-poppins text-[#3B3B3B]">
                Area Incharge
              </h3>

              <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
                <div>
                  <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                    State <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[95%] text-[12px] text-[#606060] font-poppins border border-[#D2D2D2] px-2 py-1.5 rounded-sm bg-white"
                    value={areaInchargeState}
                    onChange={(e) => setAreaInchargeState(e.target.value)}
                  >
                    <option hidden>State</option>
                    {stateList.map((item, idx) => (
                      <option key={idx} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <p className="mb-1 text-[13px] text-[#606060] font-poppins">
                    City <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[95%] text-[12px] text-[#606060] font-poppins border border-[#D2D2D2] px-2 py-1.5 rounded-sm bg-white"
                    value={areaInchargeCity}
                    onChange={(e) => setAreaInchargeCity(e.target.value)}
                  >
                    <option hidden>City</option>
                    {districtList.map((item, idx) => (
                      <option key={idx} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <h3 className="text-sm font-semibold mb-4 font-poppins text-[#3B3B3B]">
                Contact Information
              </h3>

              <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
                <div className="flex flex-col">
                  <label className="text-[13px] text-[#606060] block mb-1 font-poppins">
                    Phone <a className="text-[red]">*</a>
                  </label>
                  <div className="flex ">
                    <select
                      className="w-[60px] border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-l-sm rounded-sm text-[12px] text-[#606060] font-poppins"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                    >
                      {caseList.map((item, idx) => (
                        <option key={idx} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-r-sm text-[12px] font-poppins"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,10}$/.test(value)) {
                          setPhone(value);
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="ml-20">
                  <label className="text-[13px] text-[#606060] block mb-1 font-poppins">
                    Email <a className="text-[red]">*</a>
                  </label>
                  <input
                    type="email"
                    className="w-[250px] border border-1 border-[#D2D2D2] px-2 py-1.5 rounded-sm text-[12px] font-poppins"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {renderServicesSection()}

              <div className="mb-6 flex flex-row">
                <div className="w-[50%]">
                  <p className="text-sm font-poppins font-semibold mb-1 text-[#3B3B3B]">
                    Frequency <a className="text-[red]">*</a>
                  </p>
                  <select
                    className="w-[40%] text-[12px] text-[#606060] border border-1 border-[#D2D2D2] font-poppins px-2 py-1.5 rounded-sm"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <AddressDetailForm
                addressLine1={addressLine1}
                setAddressLine1={setAddressLine1}
                addressLine2={addressLine2}
                setAddressLine2={setAddressLine2}
                state={state}
                setState={setState}
                city={city}
                setCity={setCity}
                pinCode={pinCode}
                setPinCode={setPinCode}
                country={country}
                setCountry={setCountry}
                stateList={stateList}
                districtList={districtList}
                setCoords={setCoords}
                setFullAddress={setFullAddress}
              />
              {addressError && (
                <p className="text-[11px] text-red-500 font-poppins mt-1">
                  {addressError}
                </p>
              )}

              {onboard && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-poppins font-semibold text-[#3B3B3B]">
                      Contact Person Information
                    </h3>
                    <button
                      type="button"
                      onClick={handleAddContact}
                      className="px-4 py-1.5 border border-[#4951EC] rounded-md text-[12px] font-poppins text-[#4951EC] hover:bg-blue-50 transition-colors"
                    >
                      + Add Contact
                    </button>
                  </div>
                  <div className="border rounded-sm mb-4">
                    {contacts.map((contact, idx) =>
                      renderContactForm(contact, idx),
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddHospital;
